/**
 * OaSIS Explorer Frontend - Search Usage Hook
 * Task 30.9: Search Usage Banner
 * 
 * Lightweight hook to fetch and format search usage data for the banner.
 */

import { trpc } from '../api';
import { useAuth } from '../contexts';

export interface UseSearchUsageResult {
  /** User's plan type */
  plan: 'anonymous' | 'free' | 'pro';
  /** Number of searches used today */
  searchesUsed: number;
  /** Daily search limit */
  dailyLimit: number;
  /** ISO timestamp when usage resets */
  resetAt: string | null;
  /** Whether the query is loading */
  isLoading: boolean;
  /** Whether user is in free trial period */
  isTrialing: boolean;
  /** Days remaining in trial (null if not trialing) */
  trialDaysRemaining: number | null;
  /** Trial end date (null if not trialing) */
  trialEnd: string | null;
}

/**
 * Hook to get current search usage for the banner
 * Caches for 5 minutes to avoid excessive API calls
 */
export function useSearchUsage(): UseSearchUsageResult {
  const { user, loading: authLoading } = useAuth();
  
  // Query subscription status to determine if Pro
  const subscriptionQuery = trpc.subscription.getStatus.useQuery(undefined, {
    enabled: !!user,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    gcTime: 10 * 60 * 1000,
  });
  
  // Query usage data - only after we know the user state
  const usageQuery = trpc.features.getUsage.useQuery(
    { featureId: 'ADVANCED_SEARCH' },
    {
      enabled: !authLoading,
      staleTime: 5 * 60 * 1000, // Cache for 5 minutes
      gcTime: 10 * 60 * 1000,
      refetchOnWindowFocus: true,
    }
  );

  // Still initializing auth - don't show anything yet
  if (authLoading) {
    return {
      plan: 'free',
      searchesUsed: 0,
      dailyLimit: 6,
      resetAt: null,
      isLoading: true,
      isTrialing: false,
      trialDaysRemaining: null,
      trialEnd: null,
    };
  }

  // Determine plan type - only after subscription query completes for logged-in users
  const isPro = subscriptionQuery.data?.isPro === true;
  const isTrialing = subscriptionQuery.data?.isTrialing === true;
  const trialDaysRemaining = subscriptionQuery.data?.trialDaysRemaining ?? null;
  const trialEnd = subscriptionQuery.data?.trialEnd ?? null;
  const isAnonymous = !user;
  
  // For logged-in users, wait for subscription query to complete before showing banner
  // This prevents flash of "Free Plan" banner for Pro users
  const isSubscriptionResolved = isAnonymous || subscriptionQuery.isSuccess || subscriptionQuery.isError;
  
  let plan: 'anonymous' | 'free' | 'pro';
  if (isPro) {
    plan = 'pro';
  } else if (isAnonymous) {
    plan = 'anonymous';
  } else {
    plan = 'free';
  }

  // Extract usage data with defaults
  const usageData = usageQuery.data;
  const limit = usageData?.limit ?? (isAnonymous ? 3 : 6);
  const remaining = usageData?.remaining ?? limit;
  const searchesUsed = limit - remaining;

  return {
    plan,
    searchesUsed: Math.max(0, searchesUsed),
    dailyLimit: limit,
    resetAt: usageData?.resetAt ?? null,
    // Loading until we know the subscription status for logged-in users
    isLoading: !isSubscriptionResolved,
    isTrialing,
    trialDaysRemaining,
    trialEnd,
  };
}

export default useSearchUsage;

